import { SideLengthLabelRegion } from "util/canvas";
import {
  calculateAngle,
  calculateLineLength,
  connectLines,
  drawParallelLine,
  isLineValid,
  modifyEndPoint,
} from "util/util";

import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";

import InstructionsDetails from "components/InstructionsDetails";
import { useOptionContext } from "context/OptionContext";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { renderQuoteToCanvas } from "services/quoteRuntime";
import { Line, Point, Province } from "types/canvasTypes";

import "../App.css";
import "react-toastify/dist/ReactToastify.css";

const MIN_VIEWPORT_ZOOM = 0.5;
const MAX_VIEWPORT_ZOOM = 2.5;
const VIEWPORT_ZOOM_STEP = 0.1;
type CanvasInteractionMode = "draw" | "drag";

const clampViewportZoom = (value: number) => {
  const bounded = Math.min(MAX_VIEWPORT_ZOOM, Math.max(MIN_VIEWPORT_ZOOM, value));
  return Math.round(bounded * 100) / 100;
};

function Canvas() {
  const navigate = useNavigate();

  const {
    height,
    scale,
    lines,
    setLines,
    province,
    chalkLine,
    setChalkLine,
    gapSize,
    setGapSize,
  } = useOptionContext();

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const contextRef = useRef<CanvasRenderingContext2D | null>(null);
  const linesRef = useRef<Line[]>(lines);
  const chalkLineRef = useRef<boolean>(chalkLine);
  const gapSizeRef = useRef<number>(gapSize);
  const heightRef = useRef(height);
  const scaleRef = useRef(scale);
  const flipStateRef = useRef(false);
  const sideLengthLabelsRef = useRef<SideLengthLabelRegion[]>([]);
  const canvasWrapperRef = useRef<HTMLDivElement>(null);
  const [labelMenu, setLabelMenu] = useState<{
    lineIndex: number;
    left: number;
    top: number;
  } | null>(null);
  const [zoom, setZoom] = useState<number>(1);
  const zoomRef = useRef<number>(1);
  const [viewportOffset, setViewportOffset] = useState<Point>({ x: 0, y: 0 });
  const viewportOffsetRef = useRef<Point>({ x: 0, y: 0 });
  const [interactionMode, setInteractionMode] =
    useState<CanvasInteractionMode>("draw");

  const rerenderQuote = useCallback((nextLines: Line[] = linesRef.current) => {
    const canvas = canvasRef.current;
    const context = contextRef.current;

    if (!canvas || !context) {
      sideLengthLabelsRef.current = [];
      return;
    }

    const { sideLengthLabels } = renderQuoteToCanvas({
      context,
      canvas,
      lines: nextLines,
      glassHeight: heightRef.current,
      scale: scaleRef.current,
      chalkLine: chalkLineRef.current,
      gapSize: gapSizeRef.current,
      viewportZoom: zoomRef.current,
      viewportOffset: viewportOffsetRef.current,
    });
    sideLengthLabelsRef.current = sideLengthLabels;
  }, []);

  const cloneLine = useCallback((line: Line): Line => {
    return {
      ...line,
      start: { ...line.start },
      end: { ...line.end },
      parallelLine: line.parallelLine
        ? {
            ...line.parallelLine,
            start: { ...line.parallelLine.start },
            end: { ...line.parallelLine.end },
        }
        : undefined,
      pannels: line.pannels ? [...line.pannels] : undefined,
    };
  }, []);

  const syncAndRenderLines = useCallback(
    (nextLines: Line[]) => {
      linesRef.current = nextLines;
      setLines(nextLines);
      rerenderQuote(nextLines);
    },
    [rerenderQuote, setLines]
  );

  const getClickedSideLabel = useCallback((x: number, y: number) => {
    return sideLengthLabelsRef.current.find(
      (label) =>
        x >= label.bounds.left &&
        x <= label.bounds.right &&
        y >= label.bounds.top &&
        y <= label.bounds.bottom
    );
  }, []);

  const inferLineFlipDirection = useCallback((line: Line) => {
    if (!line.parallelLine) return flipStateRef.current;

    const dx = line.end.x - line.start.x;
    const dy = line.end.y - line.start.y;
    const length = Math.sqrt(dx * dx + dy * dy);
    if (length === 0) return flipStateRef.current;

    const nx = -dy / length;
    const ny = dx / length;
    const offsetX = line.parallelLine.start.x - line.start.x;
    const offsetY = line.parallelLine.start.y - line.start.y;
    const signedOffset = offsetX * nx + offsetY * ny;

    return signedOffset < 0;
  }, []);

  const handleEditLineAtIndex = useCallback(
    (lineIndex: number) => {
      const sourceLine = linesRef.current[lineIndex];
      if (!sourceLine) return;

      let line = cloneLine(sourceLine);
      const lineLength = calculateLineLength(line, scaleRef.current);

      const newLength = prompt(
        "Line length: ".concat(
          lineLength.toString(),
          ". Please enter a new length:"
        )
      );

      if (newLength === null || isNaN(parseFloat(newLength))) return;

      const parsedLength = parseFloat(newLength);
      if (parsedLength <= 0) return;

      const scaleFactor = parsedLength / lineLength;
      line.end.x = line.start.x + (line.end.x - line.start.x) * scaleFactor;
      line.end.y = line.start.y + (line.end.y - line.start.y) * scaleFactor;

      const isFlipped = inferLineFlipDirection(sourceLine);
      line = drawParallelLine(line, isFlipped);

      const nextLines = [...linesRef.current];
      nextLines[lineIndex] = line;
      syncAndRenderLines(nextLines);
    },
    [cloneLine, inferLineFlipDirection, syncAndRenderLines]
  );

  const handleDeleteLineAtIndex = useCallback(
    (lineIndex: number) => {
      if (lineIndex < 0 || lineIndex >= linesRef.current.length) return;

      const nextLines = linesRef.current.filter((_, index) => index !== lineIndex);
      if (nextLines.length === 0) {
        flipStateRef.current = false;
      }

      syncAndRenderLines(nextLines);
    },
    [syncAndRenderLines]
  );

  const handleClear = useCallback(() => {
    flipStateRef.current = false;
    syncAndRenderLines([]);
  }, [syncAndRenderLines]);

  const handleDeleteLast = useCallback(() => {
    if (linesRef.current.length === 0) return;
    syncAndRenderLines(linesRef.current.slice(0, -1));
  }, [syncAndRenderLines]);

  const handleFlipLast = useCallback(() => {
    if (linesRef.current.length === 0) return;

    const line = cloneLine(linesRef.current[linesRef.current.length - 1]);
    flipStateRef.current = !flipStateRef.current;
    const newLine = drawParallelLine(line, flipStateRef.current);
    const nextLines = [...linesRef.current.slice(0, -1), newLine];

    syncAndRenderLines(nextLines);
  }, [cloneLine, syncAndRenderLines]);

  const handleEditLast = useCallback(() => {
    if (linesRef.current.length === 0) return;
    handleEditLineAtIndex(linesRef.current.length - 1);
  }, [handleEditLineAtIndex]);

  const handleToggleDoor = useCallback(
    (doorCount: 1 | 2) => {
      if (linesRef.current.length === 0) return;

      const minLength =
        doorCount === 2 ? 100 : heightRef.current === 48 ? 56 : 72;
      const line = cloneLine(linesRef.current[linesRef.current.length - 1]);
      const lineLength = calculateLineLength(line, scaleRef.current);

      if (lineLength < minLength) {
        toast.error(t("errorSideTooSmall", { size: String(minLength) }));
        return;
      }

      line.numberOfDoors = line.numberOfDoors === doorCount ? 0 : doorCount;
      if (line.parallelLine) {
        line.parallelLine.numberOfDoors = line.numberOfDoors;
      }

      const nextLines = [...linesRef.current.slice(0, -1), line];
      syncAndRenderLines(nextLines);
      toast.info(doorCount === 2 ? t("addedTwoDoors") : t("addedOneDoor"));
    },
    [cloneLine, syncAndRenderLines]
  );

  const handleChalkLineChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setChalkLine(event.target.checked);
    },
    [setChalkLine]
  );

  const handleGapChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setGapSize(Number(event.target.value || 1));
    },
    [setGapSize]
  );

  const handleZoomIn = useCallback(() => {
    setZoom((currentZoom) =>
      clampViewportZoom(currentZoom + VIEWPORT_ZOOM_STEP)
    );
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom((currentZoom) =>
      clampViewportZoom(currentZoom - VIEWPORT_ZOOM_STEP)
    );
  }, []);

  const handleZoomReset = useCallback(() => {
    setZoom(1);
  }, []);

  const handleResetView = useCallback(() => {
    setZoom(1);
    setViewportOffset({ x: 0, y: 0 });
  }, []);

  useEffect(() => {
    linesRef.current = lines;
    rerenderQuote(lines);
  }, [lines, rerenderQuote]);

  useEffect(() => {
    if (labelMenu && labelMenu.lineIndex >= lines.length) {
      setLabelMenu(null);
    }
  }, [labelMenu, lines.length]);

  useEffect(() => {
    chalkLineRef.current = chalkLine;
    rerenderQuote();
  }, [chalkLine, rerenderQuote]);

  useEffect(() => {
    gapSizeRef.current = gapSize;
    rerenderQuote();
  }, [gapSize, rerenderQuote]);

  useEffect(() => {
    heightRef.current = height;
    rerenderQuote();
  }, [height, rerenderQuote]);

  useEffect(() => {
    scaleRef.current = scale;
    rerenderQuote();
  }, [scale, rerenderQuote]);

  useEffect(() => {
    zoomRef.current = zoom;
    setLabelMenu(null);
    rerenderQuote();
  }, [zoom, rerenderQuote]);

  useEffect(() => {
    viewportOffsetRef.current = viewportOffset;
    setLabelMenu(null);
    rerenderQuote();
  }, [viewportOffset, rerenderQuote]);

  useEffect(() => {
    if (interactionMode === "drag") {
      setLabelMenu(null);
    }
    if (canvasRef.current) {
      canvasRef.current.style.cursor =
        interactionMode === "drag" ? "grab" : "crosshair";
    }
  }, [interactionMode]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null) return;

    const context = canvas.getContext("2d");
    if (!context) return;

    contextRef.current = context;

    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    const desiredWidth = Math.min(screenWidth * 0.9, 1000);
    const desiredHeight = Math.min(screenHeight * 0.7, 1000);
    canvas.width = desiredWidth;
    canvas.height = desiredHeight;
    canvas.style.cursor = interactionMode === "drag" ? "grab" : "crosshair";

    const preventTouchScroll = (event: TouchEvent) => event.preventDefault();

    let currentLine: Line | null = null;
    let isDrawing = false;
    let isPanning = false;
    let panStartPoint: Point | null = null;
    let panStartOffset: Point = { ...viewportOffsetRef.current };

    const getCanvasPointFromClient = (clientX: number, clientY: number) => {
      const rect = canvas.getBoundingClientRect();
      return {
        x: ((clientX - rect.left) * canvas.width) / rect.width,
        y: ((clientY - rect.top) * canvas.height) / rect.height,
      };
    };

    const getWorldPointFromClient = (clientX: number, clientY: number) => {
      const canvasPoint = getCanvasPointFromClient(clientX, clientY);
      return {
        x: (canvasPoint.x - viewportOffsetRef.current.x) / zoomRef.current,
        y: (canvasPoint.y - viewportOffsetRef.current.y) / zoomRef.current,
      };
    };

    const getTouchCoordinates = (touch: Touch) => {
      return getWorldPointFromClient(touch.clientX, touch.clientY);
    };

    const worldToWrapperPoint = (worldX: number, worldY: number) => {
      const rect = canvas.getBoundingClientRect();
      const cssScaleX = rect.width / canvas.width;
      const cssScaleY = rect.height / canvas.height;
      return {
        x:
          canvas.offsetLeft +
          (worldX * zoomRef.current + viewportOffsetRef.current.x) * cssScaleX,
        y:
          canvas.offsetTop +
          (worldY * zoomRef.current + viewportOffsetRef.current.y) * cssScaleY,
      };
    };

    const startDrawing = (event: MouseEvent | TouchEvent) => {
      let startX = 0;
      let startY = 0;

      if (event instanceof MouseEvent) {
        if (event.button !== 0) return;

        if (interactionMode === "drag") {
          setLabelMenu(null);
          isPanning = true;
          panStartPoint = getCanvasPointFromClient(event.clientX, event.clientY);
          panStartOffset = { ...viewportOffsetRef.current };
          canvas.style.cursor = "grabbing";
          return;
        }

        const coordinates = getWorldPointFromClient(event.clientX, event.clientY);
        startX = coordinates.x;
        startY = coordinates.y;

        const clickedLabel = getClickedSideLabel(startX, startY);
        if (clickedLabel) {
          const labelCenterX =
            (clickedLabel.bounds.left + clickedLabel.bounds.right) / 2;
          const labelTop = clickedLabel.bounds.top;
          const menuPoint = worldToWrapperPoint(labelCenterX, labelTop);
          const menuTop = Math.max(10, menuPoint.y - 14);

          setLabelMenu((currentMenu) => {
            if (
              currentMenu &&
              currentMenu.lineIndex === clickedLabel.lineIndex
            ) {
              return null;
            }

            return {
              lineIndex: clickedLabel.lineIndex,
              left: menuPoint.x,
              top: menuTop,
            };
          });
          return;
        }
        setLabelMenu(null);
      } else if (event instanceof TouchEvent) {
        setLabelMenu(null);
        document.addEventListener("touchmove", preventTouchScroll, {
          passive: false,
        });

        if (event.touches.length === 0) return;

        if (interactionMode === "drag") {
          isPanning = true;
          panStartPoint = getCanvasPointFromClient(
            event.touches[0].clientX,
            event.touches[0].clientY
          );
          panStartOffset = { ...viewportOffsetRef.current };
          canvas.style.cursor = "grabbing";
          return;
        }

        const coordinates = getTouchCoordinates(event.touches[0]);
        startX = coordinates.x;
        startY = coordinates.y;
      }

      if (linesRef.current.length > 0) {
        const start = connectLines({ x: startX, y: startY }, linesRef.current);
        startX = start.x;
        startY = start.y;
      }

      currentLine = {
        start: { x: startX, y: startY },
        end: { x: startX, y: startY },
        numberOfDoors: 0,
      };

      isDrawing = true;
    };

    const drawLine = (event: MouseEvent | TouchEvent) => {
      if (isPanning) {
        if (!panStartPoint) return;

        let currentCanvasPoint: Point;
        if (event instanceof MouseEvent) {
          currentCanvasPoint = getCanvasPointFromClient(
            event.clientX,
            event.clientY
          );
        } else {
          if (event.touches.length === 0) return;
          currentCanvasPoint = getCanvasPointFromClient(
            event.touches[0].clientX,
            event.touches[0].clientY
          );
        }

        const deltaX = currentCanvasPoint.x - panStartPoint.x;
        const deltaY = currentCanvasPoint.y - panStartPoint.y;
        setViewportOffset({
          x: panStartOffset.x + deltaX,
          y: panStartOffset.y + deltaY,
        });
        return;
      }

      if (!isDrawing) {
        if (event instanceof MouseEvent) {
          if (interactionMode === "drag") {
            canvas.style.cursor = "grab";
            return;
          }

          const coordinates = getWorldPointFromClient(event.clientX, event.clientY);
          const hoveredLabel = getClickedSideLabel(coordinates.x, coordinates.y);
          canvas.style.cursor = hoveredLabel ? "pointer" : "crosshair";
        }
        return;
      }

      let mouseX = 0;
      let mouseY = 0;

      if (event instanceof MouseEvent) {
        const coordinates = getWorldPointFromClient(event.clientX, event.clientY);
        mouseX = coordinates.x;
        mouseY = coordinates.y;
      } else if (event instanceof TouchEvent) {
        if (event.touches.length === 0) return;
        const coordinates = getTouchCoordinates(event.touches[0]);
        mouseX = coordinates.x;
        mouseY = coordinates.y;
      }

      if (currentLine) {
        currentLine.end = { x: mouseX, y: mouseY };
      }

      context.setTransform(1, 0, 0, 1, 0, 0);
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.setTransform(
        zoomRef.current,
        0,
        0,
        zoomRef.current,
        viewportOffsetRef.current.x,
        viewportOffsetRef.current.y
      );

      context.strokeStyle = "black";
      context.lineWidth = 5;
      linesRef.current.forEach((line) => {
        context.beginPath();
        context.moveTo(line.start.x, line.start.y);
        context.lineTo(line.end.x, line.end.y);
        context.stroke();
      });

      if (currentLine) {
        const length = calculateLineLength(currentLine, scaleRef.current);

        context.strokeStyle = "#2563eb";
        context.lineWidth = 4;
        context.beginPath();
        context.moveTo(currentLine.start.x, currentLine.start.y);
        context.lineTo(currentLine.end.x, currentLine.end.y);
        context.stroke();

        const dx = currentLine.end.x - currentLine.start.x;
        const dy = currentLine.end.y - currentLine.start.y;
        let angle = Math.atan2(dy, dx);
        if (angle < -Math.PI / 2 || angle > Math.PI / 2) {
          angle += Math.PI;
        }

        const midpointX = (currentLine.start.x + currentLine.end.x) / 2;
        const midpointY = (currentLine.start.y + currentLine.end.y) / 2;
        const label = `${t("length")}: ${length.toFixed(2)}''`;

        context.save();
        context.translate(midpointX, midpointY);
        context.rotate(angle);

        context.font = "600 14px sans-serif";
        const textWidth = context.measureText(label).width;
        const paddingX = 8;
        const paddingY = 5;
        const boxWidth = textWidth + paddingX * 2;
        const boxHeight = 24;
        const boxX = -(boxWidth / 2);
        const boxY = -36;

        context.fillStyle = "rgba(255, 255, 255, 0.92)";
        context.strokeStyle = "rgba(37, 99, 235, 0.35)";
        context.lineWidth = 1;
        context.fillRect(boxX, boxY, boxWidth, boxHeight);
        context.strokeRect(boxX, boxY, boxWidth, boxHeight);

        context.fillStyle = "#1d4ed8";
        context.fillText(
          label,
          -(textWidth / 2),
          boxY + boxHeight - paddingY - 2
        );
        context.restore();
      }
    };

    const finishDrawing = (event: MouseEvent | TouchEvent) => {
      if (isPanning) {
        document.removeEventListener("touchmove", preventTouchScroll);
        isPanning = false;
        panStartPoint = null;
        canvas.style.cursor = interactionMode === "drag" ? "grab" : "crosshair";
        return;
      }

      if (!isDrawing) {
        document.removeEventListener("touchmove", preventTouchScroll);
        return;
      }

      let endX = 0;
      let endY = 0;

      if (event instanceof MouseEvent) {
        const coordinates = getWorldPointFromClient(event.clientX, event.clientY);
        endX = coordinates.x;
        endY = coordinates.y;
      } else if (event instanceof TouchEvent) {
        if (event.changedTouches.length === 0) {
          document.removeEventListener("touchmove", preventTouchScroll);
          isDrawing = false;
          return;
        }

        const coordinates = getTouchCoordinates(
          event.changedTouches[event.changedTouches.length - 1]
        );
        endX = coordinates.x;
        endY = coordinates.y;
      }

      if (currentLine) {
        if (!isLineValid(currentLine)) {
          currentLine = null;
          rerenderQuote();
          isDrawing = false;
          document.removeEventListener("touchmove", preventTouchScroll);
          return;
        }

        currentLine.end = { x: endX, y: endY };
        currentLine.angle = calculateAngle(currentLine);
        currentLine = modifyEndPoint(currentLine);
        currentLine = drawParallelLine(currentLine, flipStateRef.current);
        const nextLines = [...linesRef.current, currentLine];
        currentLine = null;
        syncAndRenderLines(nextLines);
      }

      document.removeEventListener("touchmove", preventTouchScroll);
      isDrawing = false;
    };

    const handleWheelZoom = (event: WheelEvent) => {
      event.preventDefault();
      const canvasPoint = getCanvasPointFromClient(event.clientX, event.clientY);
      const direction = event.deltaY < 0 ? 1 : -1;
      const currentZoom = zoomRef.current;
      const nextZoom = clampViewportZoom(
        currentZoom + direction * VIEWPORT_ZOOM_STEP
      );

      if (nextZoom === currentZoom) return;

      const worldX =
        (canvasPoint.x - viewportOffsetRef.current.x) / currentZoom;
      const worldY =
        (canvasPoint.y - viewportOffsetRef.current.y) / currentZoom;

      setZoom(nextZoom);
      setViewportOffset({
        x: canvasPoint.x - worldX * nextZoom,
        y: canvasPoint.y - worldY * nextZoom,
      });
    };

    canvas.addEventListener("touchstart", startDrawing);
    canvas.addEventListener("touchmove", drawLine);
    canvas.addEventListener("touchend", finishDrawing);
    canvas.addEventListener("touchcancel", finishDrawing);
    canvas.addEventListener("mousedown", startDrawing);
    canvas.addEventListener("mousemove", drawLine);
    canvas.addEventListener("mouseup", finishDrawing);
    canvas.addEventListener("mouseout", finishDrawing);
    canvas.addEventListener("wheel", handleWheelZoom, { passive: false });

    rerenderQuote();

    return () => {
      canvas.removeEventListener("touchstart", startDrawing);
      canvas.removeEventListener("touchmove", drawLine);
      canvas.removeEventListener("touchend", finishDrawing);
      canvas.removeEventListener("touchcancel", finishDrawing);
      canvas.removeEventListener("mousedown", startDrawing);
      canvas.removeEventListener("mousemove", drawLine);
      canvas.removeEventListener("mouseup", finishDrawing);
      canvas.removeEventListener("mouseout", finishDrawing);
      canvas.removeEventListener("wheel", handleWheelZoom);
      document.removeEventListener("touchmove", preventTouchScroll);
      canvas.style.cursor = "default";
      contextRef.current = null;
    };
  }, [getClickedSideLabel, interactionMode, rerenderQuote, syncAndRenderLines]);

  return (
    <div className="page">
      <div>
        <h1>{t("secondStep")}</h1>
        {province === Province.Quebec && (
          <InstructionsDetails title={t("canvasInstructionsTitle")}>
            <ol>
              <li>{t("canvasInstruction1")}</li>
              <li>{t("canvasInstruction2")}</li>
              <li>{t("canvasInstruction3")}</li>
            </ol>
          </InstructionsDetails>
        )}
        <ToastContainer
          position="top-center"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </div>

      <div className="flex-center" style={{ width: "100%" }}>

        <div ref={canvasWrapperRef} style={{ overflow: "visible", position: "relative" }}>
          <div className="hide-on-print"></div>
          <div className="hide-on-print" style={{ overflow: "visible" }}>
            <div className="canvas-toolbar">

              <span className="toolbar-divider" />

              <div className="toolbar-group">
                <button onClick={handleEditLast} disabled={lines.length === 0}>
                  {t("edit")}
                </button>
                <button onClick={handleFlipLast} disabled={lines.length === 0}>
                  {t("reverseSide")}
                </button>
                {height === 48 && (
                  <button
                    onClick={() => handleToggleDoor(2)}
                    disabled={lines.length === 0}
                    className="edit-button"
                  >
                    {t("addTwoDoors")}
                  </button>
                )}
                <button
                  onClick={() => handleToggleDoor(1)}
                  disabled={lines.length === 0}
                  className="edit-button"
                >
                  {t("addDoor")}
                </button>
              </div>

              <span className="toolbar-divider" />

              <div className="toolbar-group">
                <button
                  className="edit-button btn-3d"
                  onClick={() => navigate('/3d-visualization')}
                  disabled={lines.length === 0}
                  title={t("view3DTooltip")}
                >
                  🎨 {t("view3D")}
                </button>
              </div>

              <span className="toolbar-divider" />

              <div className="toolbar-group">
                <button
                  onClick={handleDeleteLast}
                  disabled={lines.length === 0}
                  className="delete-button"
                >
                  {t("deleteLastLine")}
                </button>
                <button
                  onClick={handleClear}
                  disabled={lines.length === 0}
                  className="btn-destructive"
                >
                  {t("resetBoard")}
                </button>
              </div>

              <div className="toolbar-group">
                <label>
                  <input
                    type="checkbox"
                    checked={chalkLine}
                    onChange={handleChalkLineChange}
                  />
                  {t("chalkLineMeasurement")}
                </label>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    visibility: chalkLine ? "visible" : "hidden",
                  }}
                >
                  <label>
                    {t("gapWidth")}:
                    <input
                      type="range"
                      min="0.1"
                      max="1"
                      step="0.05"
                      value={gapSize}
                      onChange={handleGapChange}
                      style={{ marginLeft: "5px" }}
                    />
                  </label>
                  {gapSize}
                </div>
              </div>

            </div>
          </div>

          <div className="canvas-stage">
            <canvas
              className="canvas-container canvas-element"
              ref={canvasRef}
              id="canvas"
              height="600px"
              width="600px"
            ></canvas>
            <div className="canvas-view-controls hide-on-print">
              <div className="canvas-view-controls__group">
                <button
                  type="button"
                  onClick={() => setInteractionMode("draw")}
                  className={`canvas-view-controls__mode-btn ${
                    interactionMode === "draw"
                      ? "canvas-view-controls__mode-btn--active"
                      : ""
                  }`}
                  aria-pressed={interactionMode === "draw"}
                >
                  Draw
                </button>
                <button
                  type="button"
                  onClick={() => setInteractionMode("drag")}
                  className={`canvas-view-controls__mode-btn ${
                    interactionMode === "drag"
                      ? "canvas-view-controls__mode-btn--active"
                      : ""
                  }`}
                  aria-pressed={interactionMode === "drag"}
                >
                  Drag
                </button>
              </div>
              <div className="canvas-view-controls__group">
                <button
                  type="button"
                  onClick={handleZoomOut}
                  disabled={zoom <= MIN_VIEWPORT_ZOOM}
                  aria-label="Zoom out"
                >
                  -
                </button>
                <button
                  type="button"
                  onClick={handleZoomReset}
                  disabled={Math.abs(zoom - 1) < 0.01}
                  aria-label="Reset zoom"
                >
                  {Math.round(zoom * 100)}%
                </button>
                <button
                  type="button"
                  onClick={handleZoomIn}
                  disabled={zoom >= MAX_VIEWPORT_ZOOM}
                  aria-label="Zoom in"
                >
                  +
                </button>
                <button
                  type="button"
                  onClick={handleResetView}
                  disabled={
                    Math.abs(zoom - 1) < 0.01 &&
                    Math.abs(viewportOffset.x) < 0.01 &&
                    Math.abs(viewportOffset.y) < 0.01
                  }
                >
                  Center
                </button>
              </div>
            </div>
            {labelMenu && (
              <div
                className="canvas-label-menu"
                style={{ left: labelMenu.left, top: labelMenu.top }}
              >
                <button
                  type="button"
                  className="canvas-label-menu__button"
                  onClick={() => {
                    handleEditLineAtIndex(labelMenu.lineIndex);
                    setLabelMenu(null);
                  }}
                >
                  {t("edit")}
                </button>
                <button
                  type="button"
                  className="canvas-label-menu__button canvas-label-menu__button--delete"
                  onClick={() => {
                    handleDeleteLineAtIndex(labelMenu.lineIndex);
                    setLabelMenu(null);
                  }}
                >
                  {t("delete")}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Canvas;
